import React, { useMemo, useState } from "react";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import { useTranslation, getEnterpriseFeatures } from "../../../i18n";
import {
  OFFER_STATUS,
  formatOfferDate,
  formatOfferPrice,
  startOfferCheckout,
} from "../customOffersApi";

function FeatureRow({ included, text, bold = false }) {
  return (
    <div className="flex items-start gap-3">
      <MaterialIcon
        name={included ? "check_circle" : "cancel"}
        filled={included}
        className={included ? "text-lg text-secondary" : "text-lg text-outline-variant"}
      />
      <span
        className={[
          "text-sm",
          included ? (bold ? "font-bold" : "font-medium") : "text-outline line-through",
        ].join(" ")}
      >
        {text}
      </span>
    </div>
  );
}

/**
 * The customer's personalised package, rendered in the Enterprise card's
 * slot on the Billing page. Pay/Subscribe goes straight to Stripe Checkout.
 */
const CustomOfferPlanCard = ({ offer, isAccountOwner, paymentsAvailable }) => {
  const { t, locale } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isPaid = offer.status === OFFER_STATUS.PAID;
  const isOneTime = offer.billingInterval === "one_time";
  const price = formatOfferPrice(offer.price, locale);

  // The card keeps everything the standard Enterprise card promises; the
  // admin's offer only replaces name/price and may add extra points on top.
  const features = useMemo(() => {
    const defaults = getEnterpriseFeatures(t).map((label) => ({ label, included: true }));
    const seen = new Set(defaults.map((f) => f.label.trim().toLowerCase()));
    const extras = offer.features.filter((f) => {
      const key = f.label.trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return [...defaults, ...extras];
  }, [offer.features, t]);
  const description = offer.description || t("billing.enterpriseTagline");

  const handleCheckout = async () => {
    if (busy || !offer.canPurchase) return;
    setBusy(true);
    setError("");
    try {
      const { checkoutUrl } = await startOfferCheckout(offer.id);
      if (!checkoutUrl) throw new Error("no url");
      window.location.assign(checkoutUrl);
    } catch (err) {
      const code = err?.data?.code;
      setError(
        code === "payments_unavailable"
          ? t("customOffer.paymentsUnavailable")
          : code === "not_account_owner"
            ? t("customOffer.ownerOnly")
            : err?.data?.detail || t("customOffer.checkoutError")
      );
      setBusy(false);
    }
  };

  let cta;
  if (isPaid) {
    cta = (
      <>
        <button
          type="button"
          disabled
          className="w-full cursor-default rounded-xl border border-outline-variant px-4 py-3 font-bold text-primary"
        >
          {t("customOffer.currentPlan")}
        </button>
        {offer.paidAt ? (
          <p className="mt-2 text-center text-[12px] text-on-surface-variant">
            {t("customOffer.activeSince", { date: formatOfferDate(offer.paidAt, locale) })}
          </p>
        ) : null}
      </>
    );
  } else if (offer.canPurchase) {
    cta = (
      <>
        <button
          type="button"
          onClick={handleCheckout}
          disabled={busy}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-4 font-bold text-on-primary shadow-[0_10px_30px_rgba(2,8,23,0.25)] transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          {busy ? (
            <>
              <MaterialIcon name="refresh" className="animate-spin text-base" />
              {t("customOffer.redirecting")}
            </>
          ) : (
            <>
              {isOneTime ? t("customOffer.payOnce") : t("customOffer.subscribe")}
              <MaterialIcon name="arrow_forward" className="text-base" />
            </>
          )}
        </button>
        <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-[12px] text-on-surface-variant">
          <MaterialIcon name="lock" className="text-[14px]" />
          {t("customOffer.stripeNote")}
          {!isOneTime ? ` · ${t("customOffer.cancelAnytime")}` : ""}
        </p>
      </>
    );
  } else {
    cta = (
      <>
        <button
          type="button"
          disabled
          className="w-full cursor-not-allowed rounded-xl border border-outline-variant px-4 py-3 font-bold text-outline"
        >
          {isOneTime ? t("customOffer.payOnce") : t("customOffer.subscribe")}
        </button>
        <p className="mt-2 text-center text-[12px] text-on-surface-variant">
          {!paymentsAvailable
            ? t("customOffer.paymentsUnavailable")
            : !isAccountOwner
              ? t("customOffer.ownerOnly")
              : t("customOffer.unavailable")}
        </p>
      </>
    );
  }

  return (
    <div className="tonal-card relative flex flex-col overflow-hidden rounded-2xl border-2 border-primary bg-surface-container-lowest p-8">
      <div className="absolute right-0 top-0">
        <div className="translate-x-10 translate-y-2 rotate-45 transform bg-primary px-10 py-1 text-[10px] font-bold uppercase text-on-primary">
          {t("customOffer.ribbon")}
        </div>
      </div>

      <div className="mb-6">
        <span className="text-xs font-bold uppercase tracking-widest text-secondary">
          {t("customOffer.eyebrow")}
        </span>
        <h3 className="mt-1 font-headline-lg text-headline-lg">{offer.title}</h3>
        <p className="mt-1 font-mono text-[11px] tracking-wide text-on-surface-variant">
          {t("customOffer.reference")} {offer.reference}
        </p>
      </div>

      <div className="mb-6">
        <div className="flex items-baseline gap-1">
          <span className="font-headline-xl text-4xl font-bold text-primary">{price}</span>
          <span className="font-body-md text-on-surface-variant">
            {t(`customOffer.intervalShort.${offer.billingInterval}`)}
          </span>
        </div>
        <p className="mt-2 whitespace-pre-line text-sm text-on-surface-variant">{description}</p>
      </div>

      {offer.limits.length > 0 ? (
        <div className="mb-6">
          <div className="flex flex-col gap-2">
            {offer.limits.map((limit, index) => (
              <div
                key={`${limit.label}-${index}`}
                className={[
                  "flex items-center justify-between gap-3",
                  index < offer.limits.length - 1 ? "border-b border-outline-variant pb-1" : "",
                ].join(" ")}
              >
                <span className="text-xs font-bold uppercase text-on-surface-variant">{limit.label}</span>
                <span className="text-right text-base font-bold">{limit.value}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mb-10 flex-grow space-y-4">
        {features.map((feature, index) => (
          <FeatureRow
            key={`${feature.label}-${index}`}
            included={feature.included}
            text={feature.label}
            bold={index === 0}
          />
        ))}
      </div>

      {error ? (
        <p className="mb-3 rounded-lg bg-error-container px-3 py-2 text-[12.5px] text-on-error-container">
          {error}
        </p>
      ) : null}

      {cta}

      {!isPaid && offer.validUntil ? (
        <p className="mt-3 text-center text-[11px] text-on-surface-variant">
          {t("customOffer.validUntil", { date: formatOfferDate(offer.validUntil, locale) })}
        </p>
      ) : null}
    </div>
  );
};

export default CustomOfferPlanCard;
